<template>
  <q-card v-if="organization?.products?.length" id="scroll-anchor" flat bordered>
    <q-card-section class="flex justify-between items-center">
      <div>
        <div class="text-h6">User Licenses</div>
        <div style="font-size: 12px">
          Update the number of users that have access to Property Reports.
        </div>
      </div>
      <div>
        <q-btn
          v-if="propertyReportSeats !== totalSeatAmount"
          no-caps
          flat
          @click="totalSeatAmount = propertyReportSeats"
          class="q-mr-sm"
        >
          Reset
        </q-btn>
        <q-btn
          id="intercom-update-seats"
          color="primary"
          no-caps
          unelevated
          :disable="
            totalSeatAmount === propertyReportSeats || totalSeatAmount < organizationMembers.length
          "
          :loading="updateSeatsLoading"
          @click="updateSeats"
        >
          Update Seats
        </q-btn>
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section class="q-gutter-y-lg">
      <div class="q-gutter-x-md" :class="{ row: $q.screen.gt.sm }">
        <div class="col">
          <div class="text-bold q-mb-sm">Total Licenses</div>
          <div>
            Represents the total number of purchased licenses plus over-allocated amounts
            (overages).
          </div>
        </div>
        <div class="col" :class="{ 'q-mt-md': $q.screen.lt.md }">
          <div class="text-right q-mb-sm">
            {{ organizationMembers.length }} / {{ propertyReportSeats }} consumed
          </div>
          <q-linear-progress
            :animation-speed="0"
            :value="organizationMembers.length / propertyReportSeats"
            color="primary"
            size="md"
            rounded
          />
        </div>
      </div>
      <div class="justify-between" :class="{ row: $q.screen.gt.xs }">
        <div>
          <div class="text-bold q-mb-sm">Total Seats</div>
          <q-btn-group class="row" flat>
            <q-btn
              outline
              icon="remove"
              square
              :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-4'"
              :disable="totalSeatAmount <= organizationMembers.length"
              @click="totalSeatAmount -= 1"
            />
            <q-input
              id="intercom-total-seats-input"
              v-model="totalSeatAmount"
              dense
              square
              style="width: 75px"
              :style="`border-top: 1px solid ${$q.dark.isActive ? 'white' : 'black'}; border-bottom: 1px solid ${$q.dark.isActive ? 'white' : 'black'}`"
              input-class="text-center text-h6"
              borderless
              @blur="
                totalSeatAmount =
                  totalSeatAmount < organizationMembers.length
                    ? organizationMembers.length
                    : parseInt(totalSeatAmount.toString())
              "
            />
            <q-btn
              id="intercom-add-seat"
              :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-4'"
              outline
              icon="add"
              square
              @click="totalSeatAmount += 1"
            />
          </q-btn-group>
          <div class="q-mt-sm">
            You currently have {{ propertyReportSeats }} seat{{
              Math.abs(propertyReportSeats) > 1 ? 's' : ''
            }}
          </div>
        </div>
        <div class="text-right" :class="{ 'q-mt-md': $q.screen.xs }">
          <div class="text-bold q-mb-sm">
            <div v-if="propertyReportsTrialDays > 0 && propertyReportsTrialEnd">
              Due {{ format(parseISO(propertyReportsTrialEnd), 'MMM d') }}
            </div>
            <div v-else>Due Today</div>
          </div>
          <div v-if="totalSeatAmount <= propertyReportSeats" class="text-h4">—</div>
          <div v-else-if="newInvoice" class="text-h4">
            {{ formatMoney(newInvoice.amount_due / 100, 2) }}
          </div>
          <q-spinner v-else class="text-h4 q-mt-sm" />
        </div>
      </div>
    </q-card-section>
  </q-card>
  <q-card v-else bordered flat class="bg-deep-orange-2">
    <div class="row justify-between q-pa-md no-wrap q-gutter-x-md">
      <div class="q-my-auto">
        The organization, <b>{{ organization?.display_name || 'Unknown' }}</b
        >, does not have an active subscription.
      </div>
      <div class="q-my-auto">
        <q-btn
          style="width: 114px"
          :to="{ name: 'Subscribe to Property Reports' }"
          color="deep-orange-10 q-px-sm"
          dense
          outline
          no-caps
        >
          7-Day Free Trial
        </q-btn>
      </div>
    </div>
  </q-card>
</template>

<script lang="ts">
import { useUserStore } from '@/stores/user'
import { useStripeStore } from '@/stores/stripe'
import { formatMoney } from '@/utils'
import { parseISO, differenceInDays, format } from 'date-fns'

export default {
  name: 'UserLicenses',
  data() {
    return {
      totalSeatAmount: 0,
      updateSeatsLoading: false,
      newInvoice: null as {
        amount_due: number
        total: number
        discount: {
          name?: string | null | undefined
          amount: number
        } | null
        subtotal: number
      } | null
    }
  },
  computed: {
    organization() {
      return useUserStore().organization
    },
    propertyReportsTrialEnd() {
      const sub = useStripeStore().subscriptions.find(
        (_) => _.products.indexOf(import.meta.env.VITE_STRIPE_PROPERTY_REPORTS_PRODUCT) > -1
      )
      return sub?.trial_end || null
    },
    propertyReportsTrialDays() {
      const trialEnd = this.propertyReportsTrialEnd
      if (trialEnd) {
        const today = new Date()
        return differenceInDays(parseISO(trialEnd), today)
      }
      return 0
    },
    organizationMembers() {
      return useUserStore().organizationMembers
    },
    propertyReportSeats() {
      return useStripeStore().propertyReportSeats
    }
  },
  watch: {
    organization() {
      this.totalSeatAmount = useStripeStore().propertyReportSeats
    },
    totalSeatAmount() {
      if (this.totalSeatAmount > this.propertyReportSeats) {
        this.newInvoice = null
        useUserStore()
          .seatUpdateInvoice(this.totalSeatAmount)
          .then((invoice) => {
            if (invoice) {
              this.newInvoice = invoice
            }
          })
      } else {
        this.newInvoice = null
      }
    }
  },
  mounted() {
    this.totalSeatAmount = useStripeStore().propertyReportSeats
  },
  methods: {
    format,
    parseISO,
    formatMoney,
    async updateSeats() {
      this.updateSeatsLoading = true
      useStripeStore()
        .updateNumberPropertyReportsSeats(this.totalSeatAmount)
        .then(() => {
          return this.$auth0.getAccessTokenSilently({ cacheMode: 'off' })
        })
        .finally(() => {
          this.updateSeatsLoading = false
        })
    }
  }
}
</script>
